/**
 * @fileoverview Inline "Edit Prospect" form for TenantDetailModal.
 * @description Only rendered before client conversion, for the current owner or a supervisor in
 * scope (see tenants-mock-store.ts's updateTenant doc comment) — "Sales users have read-only access
 * to fact-sheet fields" once the bank becomes an Active Client, with no role exception. Tenant ID
 * and Bank Tier are never editable here: the id is system-generated, and tier is always re-derived
 * from branches (shown read-only for visibility). Mirrors CreateProspectForm's full field set so a
 * prospect's profile can be completed/corrected after creation; ownership isn't editable here —
 * that's TenantDetailModal's separate Reassign Owner action. Like Add Prospect, it doesn't ask for
 * an office phone; whatever the record already holds is sent back unchanged, so saving never
 * silently clears it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  CORE_BANKING_OPTIONS,
  LEAD_RATING_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  STATE_OPTIONS,
  TENANT_BANK_TYPE_OPTIONS,
} from '@/features/sales/constants/sales-options.constants'
import { useEditTenant } from '@/features/sales/hooks/useEditTenant'
import { tierFromBranches } from '@/features/sales/lib/tier.utils'
import {
  editProspectFormSchema,
  type EditProspectFormValues,
} from '@/features/sales/schemas/edit-prospect-form.schema'
import FormField from '@/shared/components/molecules/FormField'
import SelectField from '@/shared/components/molecules/SelectField'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import DialogActions from './DialogActions'

import type { Tenant } from '@/features/sales/types/tenant.types'
import type { FormEvent } from 'react'

interface EditProspectFormProps {
  tenant: Tenant
  actingUserId: string
  actingUserRole: string
  onUpdated: () => void
  onCancel: () => void
}

/**
 * @description Appends a required-field marker to a label — every field validated as mandatory by
 * editProspectFormSchema gets one, so the user can tell at a glance which fields block saving.
 * @param {string} label - The field's translated label.
 * @returns {string} The label with a trailing " *".
 */
function req(label: string): string {
  return `${label} *`
}

/**
 * @description Builds the form's default values from the tenant's current profile.
 * @param {Tenant} tenant - The tenant being edited.
 * @returns {EditProspectFormValues} The form's initial values.
 */
function defaultValuesFor(tenant: Tenant): EditProspectFormValues {
  return {
    name: tenant.name,
    bankType: tenant.bankType,
    headOffice: tenant.headOffice,
    regNumber: tenant.regNumber,
    website: tenant.website,
    state: tenant.state,
    city: tenant.city,
    district: tenant.district,
    pincode: tenant.pincode,
    address: tenant.address,
    branches: tenant.branches,
    employees: tenant.employees ?? undefined,
    coreBanking: tenant.coreBanking,
    existingMis: tenant.existingMis,
    contactName: tenant.authorisedContact.name,
    contactDesignation: tenant.authorisedContact.designation,
    contactEmail: tenant.authorisedContact.email,
    contactPhone: tenant.authorisedContact.phone,
    leadSource: tenant.leadSource,
    leadRating: tenant.leadRating,
    notes: tenant.notes,
  }
}

/**
 * @component
 * @param {EditProspectFormProps} props - The tenant being edited and the editing user's identity.
 * @param {Tenant} props.tenant - The tenant to edit, pre-filling the form.
 * @param {string} props.actingUserId - The editing user's sales-roster id.
 * @param {string} props.actingUserRole - The editing user's active role name.
 * @param {() => void} props.onUpdated - Called after a successful update.
 * @param {() => void} props.onCancel - Called to dismiss the form without saving.
 */
function EditProspectForm({
  tenant,
  actingUserId,
  actingUserRole,
  onUpdated,
  onCancel,
}: EditProspectFormProps) {
  const { t } = useTranslation()
  const editTenant = useEditTenant(tenant.id)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditProspectFormValues>({
    resolver: zodResolver(editProspectFormSchema),
    defaultValues: defaultValuesFor(tenant),
  })

  const watchedBranches = useWatch({ control, name: 'branches' })
  const previewTier = tierFromBranches(watchedBranches || 0)
  const selectPlaceholder = t('sales.pipeline.form.selectPlaceholder')

  /**
   * @description Submits the profile changes.
   * @param {EditProspectFormValues} values - The validated form values.
   */
  const onValid = (values: EditProspectFormValues) => {
    editTenant.mutate(
      {
        name: values.name,
        bankType: values.bankType,
        headOffice: values.headOffice,
        regNumber: values.regNumber ?? '',
        website: values.website ?? '',
        state: values.state,
        city: values.city ?? '',
        district: values.district ?? '',
        pincode: values.pincode ?? '',
        address: values.address ?? '',
        branches: values.branches,
        employees: values.employees ?? null,
        coreBanking: values.coreBanking ?? '',
        existingMis: values.existingMis ?? '',
        authorisedContact: {
          name: values.contactName,
          designation: values.contactDesignation ?? '',
          email: values.contactEmail,
          phone: values.contactPhone,
          officePhone: tenant.authorisedContact.officePhone,
        },
        leadSource: values.leadSource,
        leadRating: values.leadRating,
        notes: values.notes ?? '',
        actingUserId,
        actingUserRole,
      },
      { onSuccess: onUpdated },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="edit-prospect-form"
      className="flex flex-col gap-3.5 rounded-lg border border-row-border bg-surface-hover p-3.5"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="edit-prospect-title">
        {t('sales.tenantDetail.edit.title')}
      </span>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <FormField
          id="editName"
          data-testid="edit-prospect-name-input"
          size="md"
          label={req(t('sales.tenantDetail.edit.fields.name'))}
          error={errors.name && t(errors.name.message ?? '')}
          {...register('name')}
        />
        <SelectField
          id="editBankType"
          data-testid="edit-prospect-bank-type-select"
          label={req(t('sales.tenantDetail.edit.fields.bankType'))}
          options={TENANT_BANK_TYPE_OPTIONS.map((type) => ({ value: type, label: type }))}
          error={errors.bankType && t(errors.bankType.message ?? '')}
          {...register('bankType')}
        />
        <FormField
          id="editRegNumber"
          data-testid="edit-prospect-reg-number-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.regNumber')}
          {...register('regNumber')}
        />
        <FormField
          id="editHeadOffice"
          data-testid="edit-prospect-head-office-input"
          size="md"
          label={req(t('sales.tenantDetail.edit.fields.headOffice'))}
          error={errors.headOffice && t(errors.headOffice.message ?? '')}
          {...register('headOffice')}
        />
        <FormField
          id="editWebsite"
          data-testid="edit-prospect-website-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.website')}
          {...register('website')}
        />
        <SelectField
          id="editState"
          data-testid="edit-prospect-state-select"
          label={req(t('sales.tenantDetail.edit.fields.state'))}
          options={STATE_OPTIONS.map((state) => ({ value: state, label: state }))}
          error={errors.state && t(errors.state.message ?? '')}
          {...register('state')}
        />
        <FormField
          id="editCity"
          data-testid="edit-prospect-city-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.city')}
          {...register('city')}
        />
        <FormField
          id="editDistrict"
          data-testid="edit-prospect-district-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.district')}
          {...register('district')}
        />
        <FormField
          id="editPincode"
          data-testid="edit-prospect-pincode-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.pincode')}
          {...register('pincode')}
        />
        <FormField
          id="editAddress"
          data-testid="edit-prospect-address-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.address')}
          {...register('address')}
        />
        <FormField
          id="editBranches"
          data-testid="edit-prospect-branches-input"
          size="md"
          type="number"
          label={req(t('sales.tenantDetail.edit.fields.branches'))}
          error={errors.branches && t(errors.branches.message ?? '')}
          {...register('branches', { valueAsNumber: true })}
        />
        <FormField
          id="editEmployees"
          data-testid="edit-prospect-employees-input"
          size="md"
          type="number"
          label={t('sales.tenantDetail.edit.fields.employees')}
          {...register('employees', {
            setValueAs: (value) => (value === '' ? undefined : Number(value)),
          })}
        />
        <div>
          <SelectField
            id="editBankTier"
            data-testid="edit-prospect-bank-tier-select"
            label={t('sales.tenantDetail.edit.fields.bankTier')}
            disabled
            value={previewTier}
            onChange={() => {}}
            options={[{ value: previewTier, label: previewTier }]}
          />
          <p className="mt-1.5 text-[12px] text-slate" data-testid="edit-prospect-bank-tier-hint">
            {t('sales.tenantDetail.edit.fields.bankTierHint')}
          </p>
        </div>
        <SelectField
          id="editCoreBanking"
          data-testid="edit-prospect-core-banking-select"
          label={t('sales.tenantDetail.edit.fields.coreBanking')}
          options={[
            { value: '', label: selectPlaceholder },
            ...CORE_BANKING_OPTIONS.map((system) => ({ value: system, label: system })),
          ]}
          {...register('coreBanking')}
        />
        <FormField
          id="editExistingMis"
          data-testid="edit-prospect-existing-mis-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.existingMis')}
          {...register('existingMis')}
        />
        <FormField
          id="editContactName"
          data-testid="edit-prospect-contact-name-input"
          size="md"
          label={req(t('sales.tenantDetail.edit.fields.contactName'))}
          error={errors.contactName && t(errors.contactName.message ?? '')}
          {...register('contactName')}
        />
        <FormField
          id="editContactDesignation"
          data-testid="edit-prospect-contact-designation-input"
          size="md"
          label={t('sales.tenantDetail.edit.fields.contactDesignation')}
          {...register('contactDesignation')}
        />
        <FormField
          id="editContactEmail"
          data-testid="edit-prospect-contact-email-input"
          size="md"
          type="email"
          label={req(t('sales.tenantDetail.edit.fields.contactEmail'))}
          error={errors.contactEmail && t(errors.contactEmail.message ?? '')}
          {...register('contactEmail')}
        />
        <FormField
          id="editContactPhone"
          data-testid="edit-prospect-contact-phone-input"
          size="md"
          label={req(t('sales.tenantDetail.edit.fields.contactPhone'))}
          error={errors.contactPhone && t(errors.contactPhone.message ?? '')}
          {...MOBILE_NUMBER_INPUT_PROPS}
          {...register('contactPhone')}
        />
        <SelectField
          id="editLeadSource"
          data-testid="edit-prospect-lead-source-select"
          label={req(t('sales.tenantDetail.edit.fields.leadSource'))}
          options={LEAD_SOURCE_OPTIONS.map((source) => ({ value: source, label: source }))}
          error={errors.leadSource && t(errors.leadSource.message ?? '')}
          {...register('leadSource')}
        />
        <SelectField
          id="editLeadRating"
          data-testid="edit-prospect-lead-rating-select"
          label={req(t('sales.tenantDetail.edit.fields.leadRating'))}
          options={LEAD_RATING_OPTIONS.map((rating) => ({ value: rating, label: rating }))}
          error={errors.leadRating && t(errors.leadRating.message ?? '')}
          {...register('leadRating')}
        />
      </div>

      <FormField
        id="editNotes"
        data-testid="edit-prospect-notes-input"
        size="md"
        label={t('sales.tenantDetail.edit.fields.notes')}
        {...register('notes')}
      />

      <DialogActions
        testIdPrefix="edit-prospect"
        cancelLabel={t('sales.pipeline.form.cancel')}
        confirmLabel={t('sales.tenantDetail.edit.submit')}
        onCancel={onCancel}
        confirmType="submit"
        isLoading={editTenant.isPending}
        disabled={false}
      />

      {editTenant.isError && (
        <p role="alert" className="text-[12.5px] text-danger" data-testid="edit-prospect-error">
          {editTenant.error instanceof Error ? editTenant.error.message : String(editTenant.error)}
        </p>
      )}
    </form>
  )
}

export default EditProspectForm
